import type { ExchangeRate } from "$global-providers/rxdb-provider/collections/exchange-rate";
import { EXCHANGE_RATE_CURRENCY_RX_TO_GQL } from "$global-providers/rxdb-provider/replication/exchange-rate";
import {
  type CurrencyExchangeRate,
  otherCurrencyOfPair,
} from "$routes/_lib/billing-v2/currency-exchange";
import type { BillingCurrency } from "elephant-web/gql";

const MAX_RATE_AGE_IN_DAYS = 2;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

type CurrencyExchangeRateWithSelling = Omit<CurrencyExchangeRate, "rate"> & {
  selling: string;
};

export type LatestExchangeRate = {
  otherCurrency: BillingCurrency;
  exchangeRate: CurrencyExchangeRate;
};

export const latestExchangeRateFor = (
  rates: Pick<
    ExchangeRate,
    "id" | "baseCurrency" | "quoteCurrency" | "selling" | "effectiveDate"
  >[],
  currency: BillingCurrency,
): LatestExchangeRate | null => {
  const latest = rates
    .map((rate) => ({
      id: rate.id,
      baseCurrency: EXCHANGE_RATE_CURRENCY_RX_TO_GQL[rate.baseCurrency],
      quoteCurrency: EXCHANGE_RATE_CURRENCY_RX_TO_GQL[rate.quoteCurrency],
      selling: rate.selling,
      effectiveDate: rate.effectiveDate,
    }))
    .filter((rate) => otherCurrencyOfPair(rate, currency))
    .reduce<CurrencyExchangeRateWithSelling | null>(
      (newest, rate) =>
        !newest || rate.effectiveDate > newest.effectiveDate ? rate : newest,
      null,
    );
  const otherCurrency = latest && otherCurrencyOfPair(latest, currency);
  if (!latest || !otherCurrency) return null;
  return {
    otherCurrency,
    exchangeRate: {
      id: latest.id,
      baseCurrency: latest.baseCurrency,
      quoteCurrency: latest.quoteCurrency,
      rate: latest.selling,
      effectiveDate: latest.effectiveDate,
    },
  };
};

export const isExchangeRateStale = (effectiveDate: string, now: Date) => {
  const today = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
  );
  return (
    (today - Date.parse(effectiveDate)) / MILLISECONDS_PER_DAY >
    MAX_RATE_AGE_IN_DAYS
  );
};
